<template>
  <LayoutCard rounded :title="titleText">
    <DeliveryTimeText
      v-show="!runnerServiceEnabled"
      :order-details="orderDetails"
      :order-status="orderStatus"
    />
    <OrderStatusV2
      :order-status="orderStatus"
      :is-paid="orderDetails.is_paid"
    />
    <OrderItemsExpansion
      :expanded-by-default="showProductsByDefault"
      :order-details="orderDetails"
      :restaurant-name="restaurantDetails.name"
    />
    <ReviewDrawer
      :unique-order-number="orderDetails.unique_order_number"
      :payment-uuid="paymentUuid"
      :session="orderDetails.session_token"
      :google-place-id="restaurantDetails.google_place_id"
      @rating-submitted="handleRatingSubmitted"
    >
      <template #activator="{ toggle }">
        <v-expand-transition>
          <CustomButton
            v-if="showReviewCta"
            gray
            x-small
            dark
            :data-cy="
              `food-court-order-${orderDetails.unique_order_number}-leave-review`
            "
            class="mt-4"
            @click="toggle"
          >
            {{ $t('components.review.leaveReviewCta') }}
          </CustomButton>
        </v-expand-transition>
      </template>
    </ReviewDrawer>
  </LayoutCard>
</template>
<script>
import LayoutCard from '@/components/LayoutCard.vue';
import OrderStatusV2 from '@/components/Orders/OrdersView/OrderStatusV2.vue';
import ReviewDrawer from '@/components/Reviews/ReviewDrawer.vue';
import CustomButton from '@/components/Button.vue';
import DeliveryTimeText from '@/components/Orders/DeliveryTimeText.vue';
import OrderItemsExpansion from '@/components/Orders/OrderItemsExpansion.vue';

export default {
  name: 'FoodCourtMerchantOrderStatus',
  components: {
    LayoutCard,
    OrderStatusV2,
    ReviewDrawer,
    CustomButton,
    DeliveryTimeText,
    OrderItemsExpansion,
  },
  props: {
    uniqueOrderNumber: {
      type: String,
      required: true,
    },
    showProductsByDefault: {
      type: Boolean,
      default: false,
    },
    runnerServiceEnabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      ratingSubmitted: false,
    };
  },
  computed: {
    paymentUuid() {
      return this.orderDetails.payments[0].uuid;
    },
    orderActive() {
      const order = this.orderDetails;
      return order && order.status === 'active';
    },
    titleText() {
      return this.$t('screens.foodCourtOrder.merchantOrderTitle', {
        orderId: this.orderDetails?.order_id,
        merchantName: this.restaurantDetails?.name,
      });
    },
    restaurantDetails() {
      return this.$store.getters.getFoodCourtOrderByUniqueOrderNumber(
        this.uniqueOrderNumber
      )?.restaurant;
    },
    orderDetails() {
      return this.$store.getters.getFoodCourtOrderByUniqueOrderNumber(
        this.uniqueOrderNumber
      )?.order;
    },
    someItemsDelivered() {
      const order = this.orderDetails;

      return this.orderActive && order.items.some(item => item.is_delivered);
    },
    orderInProgress() {
      const order = this.orderDetails;

      return order && this.orderActive && order.accepted_at;
    },
    orderCompleted() {
      const order = this.orderDetails;

      return order && order.status === 'closed';
    },
    orderFailed() {
      return (
        this.orderDetails.status === 'failed' ||
        this.orderDetails.status === 'rejected'
      );
    },
    orderPartiallyDelivered() {
      return this.someItemsDelivered && !this.orderCompleted;
    },
    orderStatus() {
      const order = this.orderDetails;

      const STATUS = {
        RECEIVED: 'received',
        PREPARING: 'preparing',
        PARTIALLY_DELIVERED: 'partially_delivered',
        COMPLETED: 'completed',
        FAILED: 'failed',
        RUNNER_DELIVERY_PENDING: 'runner_delivery_pending',
      };

      if (this.orderFailed) {
        return STATUS.FAILED;
      }

      if (this.runnerServiceEnabled) {
        return STATUS.RUNNER_DELIVERY_PENDING;
      }

      if (this.orderCompleted) {
        return STATUS.COMPLETED;
      }

      if (this.someItemsDelivered) {
        return STATUS.PARTIALLY_DELIVERED;
      }

      if (order.accepted_at && this.orderActive) {
        return STATUS.PREPARING;
      }

      return STATUS.RECEIVED;
    },
    hasARatingSubmitted() {
      const order = this.orderDetails;
      return order.reviews.length > 0 || this.ratingSubmitted;
    },
    showReviewCta() {
      if (this.runnerServiceEnabled) {
        return (
          this.orderStatus === 'runner_delivery_pending' &&
          !this.hasARatingSubmitted
        );
      }

      return this.orderCompleted && !this.hasARatingSubmitted;
    },
  },
  methods: {
    handleRatingSubmitted() {
      this.ratingSubmitted = true;
    },
  },
};
</script>
